import { PERMISSIONS, type PermissionKey } from './permission-keys'

/**
 * What a permission needs before it means anything.
 *
 * "Edit a user" without "View users" grants nothing: there is no screen to edit from, so the
 * cell reads as access while doing nothing. The Roles & Responsibility screen uses this map to
 * keep that state from existing — granting a child grants its parents, removing a parent removes
 * everything that depended on it — and shows both in the change panel so the admin sees it.
 *
 * Covers every module in the catalogue. Each entry names ONE parent, so the rules form a tree the
 * screen can walk in both directions without ambiguity; a permission needing two unrelated things
 * is a sign the catalogue itself needs splitting, not a reason to complicate this map.
 *
 * Belongs in the catalogue itself (a "requires" column on the permission row) once the backend is
 * next opened; the screen code will not change when it moves. Until then the test suite is what
 * keeps it honest — it fails if a key here is not in the catalogue, or if the rules form a cycle.
 */
export const PERMISSION_REQUIRES: Partial<Record<PermissionKey, PermissionKey>> = {
  // ── Users & Roles ───────────────────────────────────────────────────────
  [PERMISSIONS.USERS_USER_CREATE]: PERMISSIONS.USERS_USER_VIEW,
  [PERMISSIONS.USERS_USER_UPDATE]: PERMISSIONS.USERS_USER_VIEW,
  [PERMISSIONS.USERS_USER_ACTIVATE]: PERMISSIONS.USERS_USER_VIEW,
  [PERMISSIONS.USERS_USER_ARCHIVE]: PERMISSIONS.USERS_USER_VIEW,
  [PERMISSIONS.USERS_USER_RESEND_INVITE]: PERMISSIONS.USERS_USER_VIEW,
  [PERMISSIONS.USERS_USER_ASSIGN_ROLE]: PERMISSIONS.USERS_USER_VIEW,
  [PERMISSIONS.USERS_ROLE_MANAGE_PERMISSIONS]: PERMISSIONS.USERS_ROLE_VIEW_PERMISSIONS,
  // ── Clinic & Organization ───────────────────────────────────────────────
  [PERMISSIONS.CLINIC_CLINIC_CREATE]: PERMISSIONS.CLINIC_CLINIC_VIEW,
  [PERMISSIONS.CLINIC_CLINIC_UPDATE]: PERMISSIONS.CLINIC_CLINIC_VIEW,
  [PERMISSIONS.CLINIC_CLINIC_ARCHIVE]: PERMISSIONS.CLINIC_CLINIC_VIEW,
  [PERMISSIONS.CLINIC_LOCATIONS_MANAGE]: PERMISSIONS.CLINIC_CLINIC_VIEW,
  [PERMISSIONS.CLINIC_ROOMS_MANAGE]: PERMISSIONS.CLINIC_ROOMS_VIEW,
  // ── Providers ───────────────────────────────────────────────────────────
  // ── Patients ────────────────────────────────────────────────────────────
  [PERMISSIONS.PATIENTS_PATIENT_OPEN_CHART]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  [PERMISSIONS.PATIENTS_PATIENT_CREATE]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  [PERMISSIONS.PATIENTS_PATIENT_UPDATE]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  [PERMISSIONS.PATIENTS_PATIENT_ASSOCIATE_CLINIC]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  [PERMISSIONS.PATIENTS_PATIENT_ACTIVATE]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  [PERMISSIONS.PATIENTS_PATIENT_ARCHIVE]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  [PERMISSIONS.PATIENTS_PATIENT_INVITE]: PERMISSIONS.PATIENTS_PATIENT_VIEW,
  // ── Clinical Chart ──────────────────────────────────────────────────────
  [PERMISSIONS.CHART_VITALS_RECORD]: PERMISSIONS.CHART_VITALS_VIEW,
  [PERMISSIONS.CHART_ALLERGIES_RECORD]: PERMISSIONS.CHART_ALLERGIES_VIEW,
  [PERMISSIONS.CHART_STICKY_NOTES_MANAGE]: PERMISSIONS.CHART_STICKY_NOTES_CREATE,
  // ── Patient Documents ───────────────────────────────────────────────────
  [PERMISSIONS.DOCUMENTS_DOCUMENT_UPLOAD]: PERMISSIONS.DOCUMENTS_DOCUMENT_VIEW,
  [PERMISSIONS.DOCUMENTS_DOCUMENT_UPDATE]: PERMISSIONS.DOCUMENTS_DOCUMENT_VIEW,
  [PERMISSIONS.DOCUMENTS_DOCUMENT_ARCHIVE]: PERMISSIONS.DOCUMENTS_DOCUMENT_VIEW,
  // ── Forms & Consents ────────────────────────────────────────────────────
  [PERMISSIONS.FORMS_TEMPLATES_MANAGE]: PERMISSIONS.FORMS_TEMPLATES_VIEW,
  [PERMISSIONS.FORMS_ASSIGNMENT_EMAIL]: PERMISSIONS.FORMS_ASSIGNMENT_ASSIGN,
  [PERMISSIONS.FORMS_SUBMISSIONS_ARCHIVE]: PERMISSIONS.FORMS_SUBMISSIONS_VIEW,
  [PERMISSIONS.FORMS_ASSIGNMENT_ASSIGN]: PERMISSIONS.FORMS_TEMPLATES_VIEW,
  // ── Encounters & Clinical Notes ─────────────────────────────────────────
  [PERMISSIONS.ENCOUNTERS_ENCOUNTER_SIGN]: PERMISSIONS.ENCOUNTERS_ENCOUNTER_WRITE,
  [PERMISSIONS.ENCOUNTERS_TEMPLATES_APPLY]: PERMISSIONS.ENCOUNTERS_ENCOUNTER_WRITE,
  [PERMISSIONS.ENCOUNTERS_ENCOUNTER_START]: PERMISSIONS.ENCOUNTERS_ENCOUNTER_VIEW,
  [PERMISSIONS.ENCOUNTERS_ENCOUNTER_WRITE]: PERMISSIONS.ENCOUNTERS_ENCOUNTER_VIEW,
  [PERMISSIONS.ENCOUNTERS_ENCOUNTER_EXPORT]: PERMISSIONS.ENCOUNTERS_ENCOUNTER_VIEW,
  // ── Plan of Care ────────────────────────────────────────────────────────
  // One key per job: authoring covers create, edit and sign; lifecycle covers pause and cancel.
  [PERMISSIONS.PLAN_OF_CARE_PLAN_AUTHOR]: PERMISSIONS.PLAN_OF_CARE_PLAN_VIEW,
  [PERMISSIONS.PLAN_OF_CARE_PLAN_LIFECYCLE]: PERMISSIONS.PLAN_OF_CARE_PLAN_VIEW,
  // ── Commercial Plan ─────────────────────────────────────────────────────
  // A straight chain: the priced view is the clinical view plus money, editing needs the priced
  // view, and acting as advocate (worklist, finalize and sign) needs to be able to edit.
  [PERMISSIONS.COMMERCIAL_PLAN_PLAN_VIEW_CLIENT]: PERMISSIONS.COMMERCIAL_PLAN_PLAN_VIEW_CLINICAL,
  [PERMISSIONS.COMMERCIAL_PLAN_PLAN_UPDATE]: PERMISSIONS.COMMERCIAL_PLAN_PLAN_VIEW_CLIENT,
  [PERMISSIONS.COMMERCIAL_PLAN_PLAN_ADVOCATE]: PERMISSIONS.COMMERCIAL_PLAN_PLAN_UPDATE,
  [PERMISSIONS.COMMERCIAL_PLAN_PLAN_ASSIGN_ADVOCATE]: PERMISSIONS.PLAN_OF_CARE_PLAN_VIEW,
  // ── Charge Master & Pricing ─────────────────────────────────────────────
  [PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_CREATE]: PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_VIEW,
  [PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_UPDATE]: PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_VIEW,
  [PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_ARCHIVE]: PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_VIEW,
  [PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_IMPORT]: PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_VIEW,
  [PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_EXPORT]: PERMISSIONS.CHARGE_MASTER_CHARGE_ITEM_VIEW,
  [PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_CREATE]: PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_VIEW,
  [PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_UPDATE]: PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_VIEW,
  [PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_ACTIVATE]: PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_VIEW,
  [PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_ARCHIVE]: PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_VIEW,
  [PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_DELETE]: PERMISSIONS.CHARGE_MASTER_DISCOUNT_CODE_VIEW,
  // ── Subscriptions ───────────────────────────────────────────────────────
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW_STATS]: PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_PAUSE]: PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_CANCEL]: PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_APPROVE_PAUSE]:
    PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_RECONCILE]: PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_MANUAL_PAYMENT]:
    PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_PAY_INVOICE]: PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_WRITE_OFF]: PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  [PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_RESEND_NOTICE]:
    PERMISSIONS.SUBSCRIPTIONS_SUBSCRIPTION_VIEW,
  // ── Payments ────────────────────────────────────────────────────────────
  [PERMISSIONS.PAYMENTS_PAYMENT_TAKE_CARD]: PERMISSIONS.PAYMENTS_CARDS_VIEW,
  [PERMISSIONS.PAYMENTS_PAYMENT_REFUND]: PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.PAYMENTS_PAYMENT_CHARGEBACK]: PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.PAYMENTS_CARDS_STORE]: PERMISSIONS.PAYMENTS_CARDS_VIEW,
  [PERMISSIONS.PAYMENTS_CARDS_SET_DEFAULT]: PERMISSIONS.PAYMENTS_CARDS_VIEW,
  // ── Accounts Receivable ─────────────────────────────────────────────────
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_BALANCE,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_POST_CHARGE]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_ALLOCATE]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VOID_CHARGE]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_WRITE_OFF]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_DOCUMENTS_RECEIPT]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_DOCUMENTS_INVOICE]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  [PERMISSIONS.ACCOUNTS_RECEIVABLE_DOCUMENTS_STATEMENT]:
    PERMISSIONS.ACCOUNTS_RECEIVABLE_LEDGER_VIEW_LEDGER,
  // ── Financial Reports ───────────────────────────────────────────────────
  [PERMISSIONS.REPORTS_FINANCIAL_EXPORT]: PERMISSIONS.REPORTS_FINANCIAL_VIEW,
  // ── Inventory & Dispensing ──────────────────────────────────────────────
  [PERMISSIONS.INVENTORY_INVENTORY_MANAGE]: PERMISSIONS.INVENTORY_INVENTORY_VIEW,
  [PERMISSIONS.INVENTORY_STOCK_MOVE]: PERMISSIONS.INVENTORY_INVENTORY_VIEW,
  [PERMISSIONS.INVENTORY_STOCK_ADJUST]: PERMISSIONS.INVENTORY_INVENTORY_VIEW,
  [PERMISSIONS.INVENTORY_DISPENSE_RECORD]: PERMISSIONS.INVENTORY_INVENTORY_VIEW,
  [PERMISSIONS.INVENTORY_REPORTS_VIEW]: PERMISSIONS.INVENTORY_INVENTORY_VIEW,
  // ── Supplements & Lab Orders ────────────────────────────────────────────
  [PERMISSIONS.ORDERS_SUPPLEMENTS_CREATE]: PERMISSIONS.ORDERS_SUPPLEMENTS_VIEW,
  [PERMISSIONS.ORDERS_SUPPLEMENTS_SEND]: PERMISSIONS.ORDERS_SUPPLEMENTS_VIEW,
  [PERMISSIONS.ORDERS_LAB_ORDERS_CREATE]: PERMISSIONS.ORDERS_LAB_ORDERS_VIEW,
  [PERMISSIONS.ORDERS_LAB_ORDERS_VIEW_RESULTS]: PERMISSIONS.ORDERS_LAB_ORDERS_VIEW,
  [PERMISSIONS.ORDERS_LAB_ORDERS_AI_SUMMARY]: PERMISSIONS.ORDERS_LAB_ORDERS_VIEW,
  // ── ePrescribe ──────────────────────────────────────────────────────────
  [PERMISSIONS.EPRESCRIBE_DOSESPOT_PRESCRIBE]: PERMISSIONS.EPRESCRIBE_DOSESPOT_LAUNCH,
  // ── Communication ───────────────────────────────────────────────────────
  [PERMISSIONS.COMMUNICATION_VOICE_CALL]: PERMISSIONS.COMMUNICATION_CALL_LOGS_VIEW,
  // ── Availability & Schedule ─────────────────────────────────────────────
  [PERMISSIONS.AVAILABILITY_AVAILABILITY_UPDATE]: PERMISSIONS.AVAILABILITY_AVAILABILITY_VIEW,
  // ── Scheduling & Appointments ───────────────────────────────────────────
  [PERMISSIONS.SCHEDULING_APPOINTMENT_BOOK]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  [PERMISSIONS.SCHEDULING_APPOINTMENT_UPDATE]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  [PERMISSIONS.SCHEDULING_APPOINTMENT_RESCHEDULE]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  [PERMISSIONS.SCHEDULING_APPOINTMENT_CANCEL]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  [PERMISSIONS.SCHEDULING_APPOINTMENT_CHANGE_STATUS]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  [PERMISSIONS.SCHEDULING_APPOINTMENT_ARCHIVE]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  [PERMISSIONS.SCHEDULING_TELEHEALTH_JOIN]: PERMISSIONS.SCHEDULING_APPOINTMENT_VIEW,
  // ── Tasks ───────────────────────────────────────────────────────────────
  [PERMISSIONS.TASKS_TASK_CREATE]: PERMISSIONS.TASKS_TASK_VIEW,
  [PERMISSIONS.TASKS_TASK_UPDATE]: PERMISSIONS.TASKS_TASK_VIEW,
  [PERMISSIONS.TASKS_TASK_ARCHIVE]: PERMISSIONS.TASKS_TASK_VIEW,
}

/** Reverse index, built once: parent key → the keys that depend on it. */
const DEPENDENTS = Object.entries(PERMISSION_REQUIRES).reduce<Map<string, string[]>>(
  (index, [child, parent]) => {
    const existing = index.get(parent)
    if (existing) existing.push(child)
    else index.set(parent, [child])
    return index
  },
  new Map(),
)

/**
 * Everything `key` needs, walking up. Granting a permission must grant these too, or it grants
 * nothing. Guards against a cycle so a bad map entry cannot hang the screen.
 */
export const requiredFor = (key: string): string[] => {
  const chain: string[] = []
  let current = PERMISSION_REQUIRES[key as PermissionKey] as string | undefined
  while (current && !chain.includes(current)) {
    chain.push(current)
    current = PERMISSION_REQUIRES[current as PermissionKey] as string | undefined
  }
  return chain
}

/** Everything that depends on `key`, walking down. Removing a permission must remove these too. */
export const dependentsOf = (key: string): string[] => {
  const found: string[] = []
  const queue = [key]
  while (queue.length > 0) {
    const next = queue.shift() as string
    for (const child of DEPENDENTS.get(next) ?? []) {
      if (!found.includes(child)) {
        found.push(child)
        queue.push(child)
      }
    }
  }
  return found
}
